import { getEffectiveSelectionRange } from "../selection-tracker";
import { getString } from "./utils";

export async function addCommentCore(
  ctx: Word.RequestContext,
  args: Record<string, unknown> = {}
): Promise<
  | { ok: false; reason: "no_selection" }
  | { ok: true; added: true; commentId: string; source: "live" | "bookmark" }
> {
  const text = getString(args, "text");
  const effective = await getEffectiveSelectionRange(ctx);
  if (!effective) {
    return { ok: false, reason: "no_selection" };
  }
  const comment = effective.range.insertComment(text);
  comment.load("id");
  await ctx.sync();
  return {
    ok: true,
    added: true,
    commentId: comment.id ?? "",
    source: effective.source,
  };
}

export async function getCommentsCore(ctx: Word.RequestContext): Promise<{
  comments: Array<{
    id: string;
    authorName: string;
    content: string;
    resolved: boolean;
    anchoredText: string;
    replyCount: number;
  }>;
  total: number;
}> {
  const comments = ctx.document.comments;
  comments.load(
    "items/id,items/authorName,items/content,items/resolved,items/replies/items"
  );
  await ctx.sync();
  // Load anchored ranges in a second pass — `getRange()` returns a Range
  // that must be loaded separately.
  const ranges = comments.items.map((c) => c.getRange());
  for (const r of ranges) {
    r.load("text");
  }
  await ctx.sync();

  return {
    comments: comments.items.map((c, i) => ({
      id: c.id,
      authorName: c.authorName ?? "",
      content: c.content ?? "",
      resolved: !!c.resolved,
      anchoredText: ranges[i]?.text ?? "",
      replyCount: c.replies?.items?.length ?? 0,
    })),
    total: comments.items.length,
  };
}

async function findCommentById(
  ctx: Word.RequestContext,
  id: string
): Promise<Word.Comment> {
  const comments = ctx.document.comments;
  comments.load("items/id");
  await ctx.sync();
  const found = comments.items.find((c) => c.id === id);
  if (!found) {
    throw new Error(`Comment with id "${id}" not found.`);
  }
  return found;
}

export async function replyToCommentCore(
  ctx: Word.RequestContext,
  args: Record<string, unknown> = {}
): Promise<{ ok: true }> {
  const id = getString(args, "commentId");
  const text = getString(args, "text");
  const comment = await findCommentById(ctx, id);
  comment.reply(text);
  return { ok: true };
}

export async function resolveCommentCore(
  ctx: Word.RequestContext,
  args: Record<string, unknown> = {}
): Promise<{ ok: true }> {
  const id = getString(args, "commentId");
  const comment = await findCommentById(ctx, id);
  comment.resolved = true;
  return { ok: true };
}

export async function deleteCommentCore(
  ctx: Word.RequestContext,
  args: Record<string, unknown> = {}
): Promise<{ ok: true }> {
  const id = getString(args, "commentId");
  const comment = await findCommentById(ctx, id);
  comment.delete();
  return { ok: true };
}
